
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import request from "@/lib/request/request";
import { forEach, get, isEmpty } from "lodash-es";
import Reg from "@/lib/common/Reg";
import NumberUtil from "@/lib/common/number-util";
import Captcha from "@/lib/captcha/captcha";
import { ConnectCandidateData, ExamInfo } from "@/lib/faceVerify/face-verify";
import Config from "@/config/config";
import Toast from "./toast.vue";
import ConfirmTip from "./confirm.vue";

@Component({
  components: {
    Toast,
    ConfirmTip
  }
})
export default class FaceVerifySign extends Vue {
  @Prop({ type: ExamInfo, default: new ExamInfo() }) examInfo!: ExamInfo; //题目信息
  @Prop({ type: ConnectCandidateData, default: new ConnectCandidateData() })
  candidate!: ConnectCandidateData; // 需要反显的数据信息
  @Prop({ type: String, default: "#525FFF" }) mainColor!: String; //主色调

  loading = false;
  showName = "";
  number = "";
  isSelected = false;
  sessionId = "";
  disabledInfo = {
    showName: false, // 姓名是否 disable
    phone: false, // 手机号是否 disable
    idNumber: false // 身份证号是否 disable
  };
  signForm = {
    showName: "",
    phone: "",
    idNumber: "",
    captcha: "",
    noCheck: 1 // 忽略必填登记信息字段的非空校验，仅仅位人脸核身接口添加
  };
  signFormRules = {
    showName: [{ required: true, message: "请填写姓名", trigger: "blur" }],
    phone: [
      { required: true, message: "请填写手机号", trigger: "blur" },
      {
        validator: (rule: unknown, value: string, callback: Function) => {
          if (!Reg.isMobile(value)) {
            return callback(new Error("手机号格式校验失败"));
          } else {
            return callback();
          }
        },
        trigger: ["blur"]
      }
    ],
    idNumber: [
      { required: true, message: "请填写身份证号", trigger: "blur" },
      {
        validator: (rule: unknown, value: string, callback: Function) => {
          if (!NumberUtil.checkIdCard(value)) {
            return callback(new Error("身份证号格式校验失败"));
          } else {
            return callback();
          }
        },
        trigger: ["blur"]
      }
    ],
    captcha: [{ required: true, message: "请填写验证码", trigger: "blur" }]
  };
  tipMessage = "";

  get locationOrigin() {
    return Config.homePageHost;
  }

  mounted() {
    this.resetForm();
  }

  // 定向邀请，将手机号、姓名、身份证反显 且 不能修改。
  @Watch("candidate", { immediate: true, deep: true })
  initAndDisabledSignForm() {
    for (const key in this.candidate) {
      const item = this.candidate[key];
      if (item) {
        this.signForm[key] = item;
        this.disabledInfo[key] = true;
      }
    }
  }

  /* =================== 验证码 start =================== */
  captcha = "";
  captchaTime = 0;
  captchaTimer = 0;
  captchaSubmitting = false;
  alreadySend = false;

  async sendCaptcha() {
    try {
      await this.validatePhone();
    } catch (e) {
      return;
    }
    if (this.captchaSubmitting || this.captchaTime > 0) return;
    this.captchaSubmitting = true;
    let phoneNumber = this.signForm.phone;

    Captcha.getPromise(phoneNumber).then(
      res => {
        this.captchaSuccess();
        this.alreadySend = true;
        this.captchaSubmitting = false;
      },
      resp => {
        console.log("captcha error resp: ", resp);
        this.captchaError(resp?.data?.message);
        this.captchaSubmitting = false;
      }
    );
  }

  captchaError(message = "") {
    if (!message) {
      console.log("captchaError message: ", message);
      message = "发送验证码失败，请重试";
    }
    (this.$refs.toast as InstanceType<typeof Toast>).showToast(message);
  }

  captchaSuccess() {
    this.captchaTime = 60;
    this.captchaTimer = setInterval(() => {
      this.captchaTime = this.captchaTime - 1;
      if (this.captchaTime <= 0) {
        this.captchaTime = 0;
        clearInterval(this.captchaTimer);
      }
    }, 1000);
  }
  /* =================== 验证码 end =================== */

  candidateConnectCode = ""; // 候选人链接码
  signInfo = {} as any; // 登记信息返回值

  get candidateInfo() {
    return {
      name: this.signForm.showName,
      idNumber: this.signForm.idNumber,
      phone: this.signForm.phone,
      candidateConnectCode: this.candidateConnectCode,
      signInfo: this.signInfo
    };
  }
  async next() {
    // TODO： 测试日志是否会被上报
    console.error("next", this.signForm);
    // 1. 是否勾选协议
    if (!this.validateSelectedProtocol()) return false;
    // 2. 校验表单 => 提交登记信息
    try {
      await this.validateForm();
    } catch (e) {
      console.log(e);
      return false;
    }
    // 3. 是否已发送验证码
    if (!this.alreadySend) {
      const message = "请点击获取验证码";
      (this.$refs.toast as InstanceType<typeof Toast>).showToast(message);
      return false;
    }

    try {
      this.loading = true;
      // 1. 调用登记信息接口
      const res = await this.apiCreate();
      // 2: 获取登记信息
      console.log("res", res);
      this.candidateConnectCode = res.data.data.connectCode;
      this.signInfo = res.data.data;
      this.$emit("update", this.candidateInfo);
    } catch (err) {
      console.error("sign next catch error", err);
      if (err?.data?.code === 200006) {
        this.tipMessage = err.data.message;
        (this.$refs.confirmTip as any).show();
      } else {
        const errMsg = err?.data?.message || "请稍后再试！";
        (this.$refs.toast as InstanceType<typeof Toast>).showToast(errMsg);
      }
    } finally {
      setTimeout(() => {
        this.loading = false;
      }, 1000);
    }
  }

  // 签到完成后的处理
  afterSign() {
    // 清空验证码
    this.signForm.captcha = "";
    // 重置发送短信的倒计时状态
    this.alreadySend = false;
    clearInterval(this.captchaTimer);
    this.captchaTime = 0;
  }

  resetForm() {
    (this.$refs["signForm"] as any).resetFields();
  }

  // 提交登记信息
  apiCreate() {
    const originParams = this.examInfo.params;
    const reqParams = [];
    for (let i = 0; i < originParams.length; i++) {
      const item = originParams[i];
      if (item.name === "姓名") {
        reqParams.push({
          paramId: item.paramId,
          value: this.signForm.showName
        });
      }
      if (item.name === "手机") {
        reqParams.push({
          paramId: item.paramId,
          value: this.signForm.phone
        });
      }
      if (item.name === "身份证号") {
        reqParams.push({
          paramId: item.paramId,
          value: this.signForm.idNumber
        });
      }
    }

    const req = {
      examId: this.examInfo.examId,
      showName: this.signForm.showName,
      phone: this.signForm.phone,
      captcha: this.signForm.captcha,
      noCheck: this.signForm.noCheck,
      params: reqParams
    };
    return request.post("/aiExam/old/create", req, false);
  }

  /* =================== 校验相关 start =================== */

  validatePhone() {
    return new Promise((resolve, reject) => {
      (this.$refs["signForm"] as any).validateField(
        "phone",
        (valid: string) => {
          if (valid) {
            reject(false);
          } else {
            resolve(true);
          }
        }
      );
    });
  }

  validateSelectedProtocol() {
    if (!this.isSelected) {
      const message = "请阅读并同意隐私协议";
      (this.$refs.toast as InstanceType<typeof Toast>).showToast(message);
      return false;
    }
    return true;
  }

  validateForm() {
    return new Promise((resolve, reject) => {
      (this.$refs["signForm"] as any).validate((valid: string) => {
        if (valid) {
          resolve(true);
        } else {
          reject(false);
        }
      });
    });
  }
  /* =================== 校验相关 end =================== */
}
